import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { apiFetch } from '../jscontrol/apiClient';
import { getImageSrc } from '../../utils/appConfig';
import '../css/Category.css';

const BannerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [filterText, setFilterText] = useState('');
  const [sortKey, setSortKey] = useState('DisplayOrder');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [PAGE_SIZE, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    if (location.state?.toast) {
      showToast(location.state.toast, 'success');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBanners = async (currentPage, pageSize, search, sk, sd) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        pageIndex: currentPage,
        pageSize: pageSize,
        search: search || '',
        sortColumn: sk,
        sortBy: sd.toUpperCase(),
      });
      const { data } = await apiFetch(`/Banner/GetAll?${params}`);
      const items = data?.data ?? [];
      const count = data?.totalRecords ?? data?.totalCount ?? (Array.isArray(items) ? items.length : 0);
      setBanners(Array.isArray(items) ? items : []);
      setTotalCount(count);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners(page, PAGE_SIZE, filterText, sortKey, sortDir);
  }, [page, PAGE_SIZE, filterText, sortKey, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    try {
      await apiFetch(`/Banner/Delete/${id}`, { method: 'DELETE' });
      showToast('Banner deleted successfully.');
      fetchBanners(page, PAGE_SIZE, filterText, sortKey, sortDir);
    } catch (e) {
      showToast(e.message, 'danger');
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const goToPage = (p) => setPage(Math.max(1, Math.min(p, totalPages)));
  const handleFilter = (val) => { setFilterText(val); setPage(1); };
  const handlePageSize = (val) => { setPageSize(Number(val)); setPage(1); };

  const handleSortCol = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <i className="mdi mdi-sort ms-1" style={{ opacity: 0.3 }}></i>;
    return sortDir === 'asc'
      ? <i className="mdi mdi-sort-ascending ms-1" style={{ color: '#6f42c1' }}></i>
      : <i className="mdi mdi-sort-descending ms-1" style={{ color: '#6f42c1' }}></i>;
  };

  const renderPages = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, safePage - delta);
    const right = Math.min(totalPages - 1, safePage + delta);
    pages.push(<button key={1} className={`cat-page-btn${safePage === 1 ? ' active' : ''}`} onClick={() => goToPage(1)}>1</button>);
    if (left > 2) pages.push(<span key="l-ellipsis" className="cat-page-ellipsis">â€¦</span>);
    for (let i = left; i <= right; i++) {
      pages.push(<button key={i} className={`cat-page-btn${safePage === i ? ' active' : ''}`} onClick={() => goToPage(i)}>{i}</button>);
    }
    if (right < totalPages - 1) pages.push(<span key="r-ellipsis" className="cat-page-ellipsis">â€¦</span>);
    if (totalPages > 1) pages.push(<button key={totalPages} className={`cat-page-btn${safePage === totalPages ? ' active' : ''}`} onClick={() => goToPage(totalPages)}>{totalPages}</button>);
    return pages;
  };

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
        <AdminLink to="/admin">Dashboard</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>Banners</span>
      </p>

      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              {toast && (
                <div className={`alert alert-${toast.type} alert-dismissible d-flex align-items-center py-2 mb-3`} role="alert" style={{ fontSize: '0.875rem' }}>
                  <i className={`mdi ${toast.type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'} me-2`} style={{ fontSize: '1.1rem' }}></i>
                  <span>{toast.message}</span>
                  <button type="button" className="btn-close ms-auto" style={{ fontSize: '0.7rem' }} onClick={() => setToast(null)} aria-label="Close" />
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h4 className="card-title mb-0">Banners</h4>
                <div className="d-flex align-items-center gap-2">
                  <div className="cat-filter-wrap">
                    <i className="mdi mdi-magnify cat-filter-icon"></i>
                    <input
                      type="text"
                      className="cat-filter-input"
                      placeholder="Search banner..."
                      value={filterText}
                      onChange={e => handleFilter(e.target.value)}
                    />
                  </div>
                  <button className="cat-add-btn" onClick={() => navigate('/admin/banner/create')}>
                    <i className="mdi mdi-plus" style={{ marginRight: 6 }}></i> Add Banner
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="alert alert-danger d-flex align-items-center py-2" style={{ fontSize: '0.875rem' }}>
                  <i className="mdi mdi-alert-circle me-2" style={{ fontSize: '1.1rem' }}></i>
                  {error}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 80 }}>Image</th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('Title')}>
                          Title <SortIcon col="Title" />
                        </th>
                        <th className="cat-sort-th d-none d-md-table-cell" onClick={() => handleSortCol('RedirectUrl')}>
                          Redirect URL <SortIcon col="RedirectUrl" />
                        </th>
                        <th className="cat-sort-th d-none d-sm-table-cell" onClick={() => handleSortCol('DisplayOrder')}>
                          Order <SortIcon col="DisplayOrder" />
                        </th>
                        <th className="d-none d-md-table-cell">Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {banners.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-4 text-muted">No banners found.</td>
                        </tr>
                      ) : banners.map(b => (
                        <tr key={b.BannerId}>
                          <td>
                            {b.ImageUrl ? (
                              <img
                                src={getImageSrc(b.ImageUrl)}
                                alt={b.Title}
                                style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid #dee2e6' }}
                              />
                            ) : (
                              <div style={{ width: 60, height: 40, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="mdi mdi-image-off text-muted"></i>
                              </div>
                            )}
                          </td>
                          <td className="cat-name-cell">{b.Title}</td>
                          <td className="d-none d-md-table-cell" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {b.RedirectUrl || '-'}
                          </td>
                          <td className="d-none d-sm-table-cell">{b.DisplayOrder}</td>
                          <td className="d-none d-md-table-cell">
                            <label className={`badge ${b.IsActive ? 'badge-success' : 'badge-danger'}`}>
                              {b.IsActive ? 'Active' : 'Inactive'}
                            </label>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              style={{ border: 'none' }}
                              onClick={() => navigate(`/admin/banner/edit/${b.BannerId}`)}
                              title="Edit"
                            >
                              <i className="mdi mdi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              style={{ border: 'none' }}
                              onClick={() => handleDelete(b.BannerId)}
                              title="Delete"
                            >
                              <i className="mdi mdi-delete"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && !error && totalCount > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="cat-page-info">Rows per page:</span>
                    <select className="cat-pagesize-select" value={PAGE_SIZE} onChange={e => handlePageSize(e.target.value)}>
                      <option value={10}>10</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="cat-page-info">
                      Showing {(safePage - 1) * PAGE_SIZE + 1}â€“{Math.min(safePage * PAGE_SIZE, totalCount)} of {totalCount}
                    </span>
                  </div>
                  <div className="cat-pagination">
                    <button className="cat-page-btn" onClick={() => goToPage(1)} disabled={safePage === 1} title="First"><i className="mdi mdi-chevron-double-left"></i></button>
                    <button className="cat-page-btn" onClick={() => goToPage(safePage - 1)} disabled={safePage === 1} title="Previous"><i className="mdi mdi-chevron-left"></i></button>
                    {renderPages()}
                    <button className="cat-page-btn" onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages} title="Next"><i className="mdi mdi-chevron-right"></i></button>
                    <button className="cat-page-btn" onClick={() => goToPage(totalPages)} disabled={safePage === totalPages} title="Last"><i className="mdi mdi-chevron-double-right"></i></button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BannerPage;
